import AddIcon from "@mui/icons-material/Add"
import ArchiveIcon from "@mui/icons-material/Archive"
import EditIcon from "@mui/icons-material/Edit"
import { Box, Container, FormControl, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, Typography } from "@mui/material"
import Button from "@mui/material/Button"
import ButtonGroup from "@mui/material/ButtonGroup"
import {
  GridActionsCellItem,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
} from "@mui/x-data-grid"
import { FC, useEffect, useState } from "react"
import { Helmet } from "react-helmet-async"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { RootState } from "../../app/store"
import ServerGrid, { ServerGridProps } from "../../components/common/ServerGrid"
import { FilterOperator } from "../../constants/enums"
import {
  ADD_PRICING_RULE,
  UPDATE_PRICING_RULE,
} from "../../constants/routeConstants"
import {
  setGridPaginationModel,
  setPricingRulesColumnVisibilityModel,
  setGridSortModel,
  usePaginationModel,
  usePricingRulesColumnVisibilityModel,
  useSortModel,
} from "../../features/gridSlice/gridSlice"
import {
  useArchiveRuleMutation,
  useDeleteRuleMutation,
  useGetManufacturerQuery,
  useGetRulesQuery,
  useUploadPricingRuleCsvMutation,
} from "../../services/API/pricingRuleAPI"
import { useGetCustomerPriceLevelQuery as useGetCustomerPriceLevelQueryEverest } from "../../services/API/customerPriceLevel"
import { skipToken } from "@reduxjs/toolkit/query"
import { formatDateToLocalTime } from "../../utils/formatDate"
import { getApiErrorMessage } from "../../utils/getApiErrorMessage"
import { getPageNumbers } from "../../utils/getPageNumbers"

import { confirmAlert } from "react-confirm-alert"
import "react-confirm-alert/src/react-confirm-alert.css"
import { ARCHIVE } from "../../constants/appConstants"
import { getPricingRuleColumns } from "../../utils/gridColumns/pricingRuleCoulmns"
import FileUpload from "./components/FileUpload"

const PricingRules: FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const pricingRuleColumns = prepareColumns()

  const FILTERS_STORAGE_KEY = 'pricingRules.filters'
  const getSavedFilters = (): Partial<{
    customerPriceLevelId: string
    manufacturerCode: string
    duration: 'All' | 'Permanent' | 'Temporary'
  }> => {
    try {
      const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY)
      return raw ? JSON.parse(raw) : {}
    } catch {
      return {}
    }
  }

  const toApiSortField = (field?: string) => {
    if (!field) return undefined
    const map: Record<string, string> = {
      // Mongo stores the rule name as PascalCase per server filters
      ruleName: "RuleName",
      // If ever sorting by ruleId, Mongo field is _id
      ruleId: "_id",
    }
    return map[field] ?? field
  }

  const dmsType = useAppSelector((state: RootState) => state.auth.dmsType)
  const dmsDealerId = useAppSelector(
    (state: RootState) => state.auth.dmsDealerId,
  )
  const companyDealerId = useAppSelector(
    (state: RootState) => state.auth.companyDealerId,
  )
  const dealerGuid = useAppSelector(
    (state: RootState) => state.auth.dealerGuid,
  )
  const paginationModel = usePaginationModel()
  const setPaginationModel = (paginationModel: PaginationModel) =>
    dispatch(setGridPaginationModel(paginationModel))

  const onColumnVisibilityModelChange = (model: GridColumnVisibilityModel) =>
    dispatch(setPricingRulesColumnVisibilityModel(model))

  const columnVisibilityModel = usePricingRulesColumnVisibilityModel()

  const sortModel = useSortModel()

  const [rows, setRows] = useState<Rule[]>([])
  const [filterValue, setFilterValue] = useState("")
  const [filterOperator, setFilterOperator] = useState(FilterOperator.Equals)
  const [rowCountState, setRowCountState] = useState(0)

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([])

  const [filterActive, setFilterActive] = useState<boolean | null>(true)
  const [filterInactive, setFilterInactive] = useState<boolean | null>(null)
  const [filterArchive, setFilterArchive] = useState<boolean | null>(null)

  const [selectedCustomerPriceLevel, setSelectedCustomerPriceLevel] = useState<string>(() => getSavedFilters().customerPriceLevelId ?? "")
  const [selectedManufacturer, setSelectedManufacturer] = useState<string>(() => getSavedFilters().manufacturerCode ?? "")
  const [selectedDuration, setSelectedDuration] = useState<'All' | 'Permanent' | 'Temporary'>(() => {
    const d = getSavedFilters().duration
    return d === 'Permanent' || d === 'Temporary' || d === 'All' ? d : 'All'
  })

  const [filter, setFilter] = useState<GetRulesSearchCriteria>(() => {
    const saved = getSavedFilters()
    return {
      dmsType,
      dmsDealerId,
      ruleId: undefined,
      sortDir:
        (sortModel?.[0]?.sort === "asc"
          ? (1 as any)
          : sortModel?.[0]?.sort === "desc"
          ? (0 as any)
          : undefined),
      sortField: toApiSortField(sortModel?.[0]?.field),
      isActive: true,
      isArchive: false,
      includeRuleDetails: true,
      customerPriceLevelId: saved.customerPriceLevelId || undefined,
      manufacturerCode: saved.manufacturerCode || undefined,
      duration: saved.duration && saved.duration !== 'All' ? saved.duration : undefined,
    }
  })

  /*
   * API calls
   * */

  const {
    data: PricingRules = {},
    isFetching: PricingRulesIsFetching,
    isSuccess: PricingRulesIsSuccess,
    refetch: refetchPricingRules,
  } = useGetRulesQuery(
    {
      ...filter,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const [archiveRule] = useArchiveRuleMutation()
  const [deleteRule] = useDeleteRuleMutation()

  const { data: customerPriceLevels = [] } = useGetCustomerPriceLevelQueryEverest(
    dealerGuid ? { dealerGuid } : skipToken,
  )

  const { data: manufacturers = [] } = useGetManufacturerQuery({
    companyDealerId,
  })

  /*
   * Effects
   * */

  const toMMDDYYYY = (date?: string | Date | null) => {
    if (!date) return ""
    const d = typeof date === "string" ? new Date(date) : new Date(date)
    return d.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    })
  }

  useEffect(() => {
    setRowCountState(PricingRules?.totalRecordsCount || 0)

    if (PricingRules?.data) {
      const transformedRows = PricingRules?.data?.map((rule: any) => ({
        ...rule,
        id: rule.ruleId,
          startDate: formatDateToLocalTime(rule.startDate),
          endDate: formatDateToLocalTime(rule.endDate),
      }))

      setRows(transformedRows)
    }
  }, [PricingRulesIsSuccess, PricingRules])

  useEffect(() => {
    setRowCountState((prev: any) =>
      PricingRules?.totalRecordsCount !== undefined
        ? PricingRules?.totalRecordsCount
        : prev,
    )
  }, [paginationModel])

  /*
   * Handlers
   * */

  // Handles the click event for filter buttons
  const handleFilterClick = (filter: string | null) => {
    setFilterActive(filter === "active" ? true : null)
    setFilterInactive(filter === "inactive" ? false : null)
    setFilterArchive(filter === "archive" ? true : null)

    if (filter === "active") {
      setFilter((prevState: GetRulesSearchCriteria) => ({
        ...prevState,
        isActive: true,
        isArchive: false,
      }))
    }

    if (filter === "inactive") {
      setFilter((prevState: GetRulesSearchCriteria) => ({
        ...prevState,
        isActive: false,
        isArchive: false,
      }))
    }

    if (filter === "archive") {
      setFilter((prevState: GetRulesSearchCriteria) => ({
        ...prevState,
        isActive: false,
        isArchive: true,
      }))
    }
  }

  function prepareColumns() {
    const setActions = (params: GridRowParams<any>) => {
      return [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          title="Edit"
          onClick={() => {
            navigate(`${UPDATE_PRICING_RULE}/${params.row.ruleId}`)
          }}
          color="primary"
        />,

        <GridActionsCellItem
          icon={<ArchiveIcon />}
          label="Archive Toggle Button"
          title="Archive Toggle Button"
          onClick={() =>
            confirmationDialogueHandler(
              params.row.ruleId,
              ARCHIVE,
              params.row.archive,
            )
          }
          color="primary"
        />,
      ]
    }

    return getPricingRuleColumns(setActions)
  }

  const confirmationDialogueHandler = (
    id: any,
    name: string,
    isArchive?: boolean,
  ) => {
    confirmAlert({
      title: "Confirmation",
      message: isArchive
        ? "Are you sure you want to unarchive?"
        : "Are you sure you want to archive?",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            name == ARCHIVE ? handleArchive(id, isArchive) : handleDelete(id)
          },
        },
        {
          label: "No",
          onClick: () => {
            return
          },
        },
      ],
    })
  }

  const handleArchive = async (ruleId: any, isArchive?: boolean) => {
    try {
      let response = await archiveRule({
        archive: !isArchive,
        ruleIds: [ruleId],
      })

      // Handle the error
      if ("error" in response) throw response

      // Handle the success response
      toast.success(
        isArchive
          ? "Rule Unarchived Successfully"
          : "Rule Archived Successfully",
      )
      refetchPricingRules()
    } catch (err) {
      if ((err as any)?.error?.status == 401) {
        toast.error("User Unauthorized, Please Refresh the Page.")
      } else {
        toast.error(getApiErrorMessage(err))
      }
    }
  }

  const handleDelete = async (ruleId: any) => {
    try {
      let response = await deleteRule([ruleId])

      // Handle the error
      if ("error" in response) throw response

      // Handle the success response
      toast.success("Rule Deleted Successfully")
      refetchPricingRules()
    } catch (err) {
      if ((err as any)?.error?.status == 401) {
        toast.error("User Unauthorized, Please Refresh the Page.")
      } else {
        toast.error(getApiErrorMessage(err))
      }
    }
  }

  const totalPages = Math.ceil(
    (PricingRules?.totalRecordsCount ?? rowCountState) /
      paginationModel.pageSize,
  )

  const pageNumbers = getPageNumbers(totalPages, paginationModel.page + 1)

  const onFilterChange = (model: GridFilterModel) => {
    // const newFilter: GetRulesSearchCriteria = {
    // ...(model.items.length > 0 && {
    //   id: model.items[0].field === "id" ? model.items[0].value : undefined,
    //   byName:
    //     model.items[0].field === "productName"
    //       ? model.items[0].value
    //       : undefined,
    // }),
    // }
    // setFilter(newFilter)
    // setPaginationModel({
    //   ...paginationModel,
    //   page: 0,
    // })
  }

  const onSortModelChange = (model: GridSortModel) => {
    dispatch(setGridSortModel(model))
    setFilter((prevState: GetRulesSearchCriteria) => ({
      ...prevState,
      sortField: toApiSortField(model?.[0]?.field) ?? undefined,
      sortDir:
        model?.[0]?.sort === "asc"
          ? (1 as any)
          : model?.[0]?.sort === "desc"
          ? (0 as any)
          : undefined,
    }))
    setPaginationModel({
      ...paginationModel,
      page: 0,
    })
  }

  const handlePricingRuleFormClick = () => {
    navigate(ADD_PRICING_RULE)
  }

  const handleCustomerPriceLevelChange = (event: SelectChangeEvent<string>) => {
    setSelectedCustomerPriceLevel(event.target.value)
  }

  const handleManufacturerChange = (event: SelectChangeEvent<string>) => {
    setSelectedManufacturer(event.target.value)
  }

  const handleApplyFilters = () => {
    setFilter((prevState: GetRulesSearchCriteria) => ({
      ...prevState,
      customerPriceLevelId: selectedCustomerPriceLevel || undefined,
      manufacturerCode: selectedManufacturer || undefined,
      duration: selectedDuration === 'All' ? undefined : selectedDuration,
    }))
    // Persist for this session
    const payloadToSave = {
      customerPriceLevelId: selectedCustomerPriceLevel || "",
      manufacturerCode: selectedManufacturer || "",
      duration: selectedDuration,
    }
    try {
      sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(payloadToSave))
    } catch {}
    setPaginationModel({
      ...paginationModel,
      page: 0,
    })
  }

  const serverGridProps: ServerGridProps = {
    rows,
    columns: pricingRuleColumns,
    onFilterChange,
    onSortModelChange,
    paginationModel,
    rowCount: PricingRules?.totalRecordsCount ?? rowCountState ?? 0,
    setPaginationModel,
    rowSelectionModel,
    setRowSelectionModel,
    isLoading: PricingRulesIsFetching,
    keepNonExistentRowsSelected: true,
    pageNumbers,
    totalPages,
    filterOperator,
    setFilterOperator,
    filterValue,
    setFilterValue,
    hiddenColumns: ["ruleId", "startDateTime", "endDateTime"],
    disableColumnFilter: true,
    columnVisibilityModel,
    onColumnVisibilityModelChange,
    sortModel,
  }

  return (
    <>
      <Helmet>
        <title> Pricing Rules </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" p={1} mb={1}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Pricing Rules
            </Typography>

                      <ButtonGroup variant="contained" aria-label="Basic button group">
                          <Button
                              onClick={() => handleFilterClick("active")}
                              variant={filterActive ? "contained" : "outlined"}
                          >
                              Active
                          </Button>

                          <Button
                              onClick={() => handleFilterClick("inactive")}
                              variant={
                                  filterInactive !== null && !filterInactive
                                      ? "contained"
                                      : "outlined"
                              }
                          >
                              Inactive
                          </Button>

                          <Button
                              onClick={() => handleFilterClick("archive")}
                              variant={filterArchive ? "contained" : "outlined"}
                          >
                              Archive
                          </Button>
                      </ButtonGroup>

            <Box>
              <FileUpload
                useUploadMutation={useUploadPricingRuleCsvMutation}
                refetchData={refetchPricingRules}
              />

              <IconButton
                title="Add new Quantity Discount"
                onClick={handlePricingRuleFormClick}
                sx={{
                  ml: 2,
                  p: 1,
                  color: "white",
                  backgroundColor: "primary.main",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                }}
              >
                <AddIcon fontSize="large" />
              </IconButton>
            </Box>
          </div>
        </Stack>

        {/* Filter Section */}
        <Box sx={{ mb: 2, p: 2, bgcolor: "background.default", borderRadius: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 250 }}>
              <InputLabel id="customer-price-level-label">Customer Price Level</InputLabel>
              <Select
                labelId="customer-price-level-label"
                id="customer-price-level-select"
                value={selectedCustomerPriceLevel}
                label="Customer Price Level"
                onChange={handleCustomerPriceLevelChange}
                size="small"
                MenuProps={{ PaperProps: { style: { maxHeight: 10 * 48, overflowY: 'auto' } } }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {customerPriceLevels.map((level: any) => (
                  <MenuItem key={level.customerPriceLevelId} value={level.customerPriceLevelId}>
                    {level.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 250 }}>
              <InputLabel id="manufacturer-label">Manufacturer</InputLabel>
              <Select
                labelId="manufacturer-label"
                id="manufacturer-select"
                value={selectedManufacturer}
                label="Manufacturer"
                onChange={handleManufacturerChange}
                size="small"
                MenuProps={{ PaperProps: { style: { maxHeight: 10 * 48, overflowY: "auto" } } }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {manufacturers.map((mfr: any) => (
                  <MenuItem key={mfr.manufacturerId} value={mfr.manufacturerId}>
                    {mfr.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <ButtonGroup
              size="small"
              aria-label="duration filter"
              sx={{
                '& .MuiButton-root': { color: '#efb310' },
                '& .MuiButton-outlined': {
                  borderColor: '#efb310',
                  '&:hover': { borderColor: '#d19f0f', color: '#d19f0f' },
                },
                '& .MuiButton-contained': {
                  bgcolor: '#efb310',
                  color: 'black',
                  '&:hover': { bgcolor: '#d19f0f' },
                },
              }}
            >
              <Button
                variant={selectedDuration === 'Permanent' ? 'contained' : 'outlined'}
                onClick={() => setSelectedDuration('Permanent')}
              >
                Permanent
              </Button>
              <Button
                variant={selectedDuration === 'Temporary' ? 'contained' : 'outlined'}
                onClick={() => setSelectedDuration('Temporary')}
              >
                Temporary
              </Button>
              <Button
                variant={selectedDuration === 'All' ? 'contained' : 'outlined'}
                onClick={() => setSelectedDuration('All')}
              >
                All
              </Button>
            </ButtonGroup>

            <Button
              variant="contained"
              size="small"
              onClick={handleApplyFilters}
              sx={{}}
            >
              Apply
            </Button>
          </Stack>
        </Box>

        <ServerGrid {...serverGridProps} />
      </Container>
    </>
  )
}

export default PricingRules
